import React from 'react'
import { isEqual } from 'lodash'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import TypeSelect from '../../TypeSelect'
import Spacer from '@SonarCX/components/atoms/Spacer'
import Button from '@SonarCX/components/atoms/Button2'
import CustomFieldsList from '../../CustomFieldsList'
import NoGroups from './NoGroups'
import Group from './Group'
import NewGroupModal from './NewGroupModal'

import { ObjectType, nameMap } from '../../types'
import { moveItem } from '@SonarCX/utilities'
import { cleanGroup } from './Group/helpers'
import { batchUpdateCustomFieldGroups } from '@SonarCX/slices/customFields/groups'
import useCustomFieldGroups from '@SonarCX/hooks/useCustomFieldGroups'
import useStyles from '../styles'

type GroupSettingsProps = {
  tab: ObjectType
}

const GroupSettings: React.FC<GroupSettingsProps> = ({ tab }) => {
  const dispatch = useAppDispatch()

  const { allGroups: group } = useCustomFieldGroups(tab)

  const [type, setType] = React.useState('null')
  const [newGroupModalOpen, setNewGroupModalOpen] = React.useState(false)

  const filteredGroups = React.useMemo(() => {
    const filterType = type === 'null' ? null : type
    const filtered = group.filter(group => group.type_id === filterType)
    return filtered.sort((a, b) => a.order - b.order)
  }, [group, type])

  const classes = useStyles()

  const handleGroupMove = React.useCallback(
    (source: string, target: string, above: boolean) => {
      const moved = moveItem(
        filteredGroups,
        source,
        target,
        above,
        item => item.id
      )

      if (isEqual(filteredGroups, moved)) {
        return
      }

      let order = 0
      const toUpload = moved.map(group => {
        const cleaned = cleanGroup(group)
        cleaned.order = order
        order += 10
        return cleaned
      })

      dispatch(batchUpdateCustomFieldGroups(toUpload))
    },
    [filteredGroups, dispatch]
  )

  return (
    <>
      <div className={classes.container}>
        <div className={classes.list}>
          <div className={classes.controls}>
            <TypeSelect
              title='groups'
              tab={tab}
              type={type}
              setType={setType}
            />

            <Spacer />

            <div className={classes.buttonRoot}>
              <Button
                className={classes.button}
                onClick={() => setNewGroupModalOpen(true)}
              >
                + Group
              </Button>
            </div>
          </div>

          <div className={classes.list}>
            {filteredGroups.length === 0 ? (
              <NoGroups />
            ) : (
              filteredGroups.map((group, i) => (
                <Group
                  key={group.id}
                  group={group}
                  isFirst={i === 0}
                  isLast={i === filteredGroups.length - 1}
                  onMove={handleGroupMove}
                />
              ))
            )}
          </div>
        </div>

        <CustomFieldsList tab={tab} />
      </div>

      {newGroupModalOpen && (
        <NewGroupModal
          title={nameMap[tab]}
          object_type={tab}
          type_id={type}
          onClose={() => setNewGroupModalOpen(false)}
        />
      )}
    </>
  )
}

export default GroupSettings
